/* VariantSelector - Type tabs (Kluit, Pot, etc.) and size chips for product pages
 * Shows available variant types as tabs and sizes as clickable chips
 * Navigates to the selected variant URL on selection
 * Current variant is highlighted
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Flower2 } from 'lucide-react';

interface VariantOption {
  id: string;
  size: string;
  type: string;
  slug: string;
  stock_quantity: number;
}

interface VariantSelectorProps {
  variants: VariantOption[];
  currentVariantId: string;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  'Kluit': <Package className="w-4 h-4" />,
  'Pot': <Flower2 className="w-4 h-4" />,
};

const TYPE_ORDER = ['Kluit', 'Pot', 'Kale wortel', 'Haagelement'];

export default function VariantSelector({ variants, currentVariantId }: VariantSelectorProps) {
  const navigate = useNavigate();

  const currentVariant = variants.find(v => v.id === currentVariantId);
  const selectedType = currentVariant?.type || '';

  const availableTypes = useMemo(() => {
    const types = Array.from(new Set(variants.map(v => v.type)));
    return types.sort((a, b) => {
      const ai = TYPE_ORDER.indexOf(a);
      const bi = TYPE_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [variants]);

  const sizesForType = useMemo(() => {
    return variants
      .filter(v => v.type === selectedType)
      .sort((a, b) => {
        const numA = parseInt(a.size) || 0;
        const numB = parseInt(b.size) || 0;
        return numA - numB;
      });
  }, [variants, selectedType]);

  const handleTypeSelect = (type: string) => {
    if (type === selectedType) return;
    const firstOfType = variants
      .filter(v => v.type === type)
      .sort((a, b) => (parseInt(a.size) || 0) - (parseInt(b.size) || 0))[0];
    if (firstOfType) {
      navigate(`/${firstOfType.slug}`);
    }
  };

  const handleSizeSelect = (variant: VariantOption) => {
    if (variant.id === currentVariantId) return;
    navigate(`/${variant.slug}`);
  };

  if (variants.length <= 1) return null;

  return (
    <div className="space-y-4">
      {/* Type tabs */}
      {availableTypes.length > 1 && (
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-2 block">Type</label>
          <div className="flex gap-2 flex-wrap">
            {availableTypes.map((type) => {
              const isActive = type === selectedType;
              return (
                <button
                  key={type}
                  onClick={() => handleTypeSelect(type)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 border-2 ${
                    isActive
                      ? 'bg-jv-primary text-white border-jv-primary shadow-md'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-jv-primary/50 hover:text-jv-primary'
                  }`}
                >
                  {TYPE_ICONS[type] || null}
                  {type}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Size chips */}
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-2 block">
          Afmeting
        </label>
        <div className="flex gap-2 flex-wrap">
          {sizesForType.map((variant) => {
            const isActive = variant.id === currentVariantId;
            const isOutOfStock = variant.stock_quantity <= 0;
            return (
              <button
                key={variant.id}
                onClick={() => handleSizeSelect(variant)}
                disabled={isOutOfStock}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all duration-200 border-2 ${
                  isActive
                    ? 'bg-jv-primary text-white border-jv-primary shadow-md'
                    : isOutOfStock
                    ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed line-through'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-jv-primary/50 hover:text-jv-primary'
                }`}
              >
                {variant.size.trim()}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
